import React from "react";
import { cls } from "@wss/cls";
import { User } from "~/common/Store/models/User";
import Price from "./Price";
import { SelectedProduct } from "~/common/Store/models/ProductListing";
import PlusMemberDiscount from "./PlusMemberDiscount";
import applyBelowMapPricing from "./applyBelowMapPricing";
import applyPlusPricing from "../applyPlusPricing";
import ExclusivePlusPricing from "./Map/ExclusivePlusPricing";
import itemHasMapStyle from "../itemHasMapStyle";
import SaleBadgeAndPricing from "~/common/SaleBadgeAndPricing";
import QuantityDiscountsTable from "./QuantityDiscountsTable";
import getLowestPrice from "../getLowestPrice";
import DivideByPricing from "./DivideByPricing";

interface QuantityDiscountsProps {
  selectedProduct: SelectedProduct;
  user: User;
  productListingView?: string;
  hideQuantityDiscountTable?: boolean;
  displaySalePrice?: boolean;
  setIsQuantityDiscountPopoverOpen?: React.Dispatch<
    React.SetStateAction<string>
  >;
}

function getApplicableQuantityDiscountPrice(
  price: SelectedProduct["price"],
  startBuy: number,
) {
  const { quantityDiscounts, hasQuantityDiscounts, price: p1Price } = price;
  const hasLotDiscounts = quantityDiscounts.some(
    (discount) => discount.isLotDiscount,
  );

  if (hasQuantityDiscounts && startBuy > 1) {
    if (hasLotDiscounts) {
      return p1Price;
    }
    // Find discount that matches the min/must buy quantity.
    const matchingDiscount = quantityDiscounts.find(
      (discount) =>
        startBuy >= discount.startQuantity &&
        (discount.endQuantity == null || startBuy <= discount.endQuantity),
    );
    if (matchingDiscount) {
      return matchingDiscount.price;
    }
  }
  return -1;
}

const QuantityDiscounts = ({
  selectedProduct,
  user,
  productListingView,
  hideQuantityDiscountTable = false,
  displaySalePrice = false,
  setIsQuantityDiscountPopoverOpen,
}: QuantityDiscountsProps) => {
  const {
    price,
    unitOfMeasure,
    activeSale,
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    showBelowMinimumAdvertisedPriceForLoggedInCustomers,
    divideQuantity,
    divideByUnit,
    startBuy,
    mustBuy,
    itemNumber,
    link,
  } = selectedProduct;

  const { quantityDiscounts } = price;

  const hasMAPStyle = itemHasMapStyle(price, activeSale);
  const isUserQualifiedForMapPricing = applyBelowMapPricing(
    {
      price,
      showBelowMinimumAdvertisedPriceForLoggedInCustomers,
      showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    },
    user,
  );
  const showExclusivePlusPricing =
    user.hasWebPlus &&
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers &&
    hasMAPStyle &&
    !!price.minimumAdvertisedPriceProperties.price;

  // Show Plus Pricing if p5 price is available and it's the lowest price.
  // If MAP style, checks for below MAP qualification and active sale.
  const showPlusPricing = applyPlusPricing(
    price,
    activeSale,
    hasMAPStyle,
    !!isUserQualifiedForMapPricing,
  );

  // Shows the lowest price: regular, sale, or MAP.
  const lowestPrice = getLowestPrice(
    price,
    user,
    activeSale,
    showBelowMinimumAdvertisedPriceForLoggedInCustomers,
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
  );

  const discountsWithLowerPrice = quantityDiscounts.filter(
    (quantity) => quantity.price < lowestPrice,
  );

  const discountsWithLowerPriceAndHigherStartBuy =
    discountsWithLowerPrice.filter(
      (discount) =>
        discount.isLotDiscount ||
        discount.startQuantity >= startBuy ||
        (discount.endQuantity && discount.endQuantity > startBuy),
    );

  const applicableQuantityDiscountPrice = getApplicableQuantityDiscountPrice(
    price,
    startBuy,
  );

  // Only use quantity discount price if it's needed and lower than the lowest price.
  const hasApplicableQuantityDiscountPrice =
    applicableQuantityDiscountPrice !== -1 &&
    (!isUserQualifiedForMapPricing ||
      (isUserQualifiedForMapPricing &&
        applicableQuantityDiscountPrice < lowestPrice));

  const hasSalePriceButNoDivideByQuantity = displaySalePrice && !divideQuantity;
  const hasNoSalePriceButHasDivideByQuantity =
    !displaySalePrice && !!divideQuantity;

  // Show discount table if sale/divide logic matches and discounts exist.
  const shouldDisplayQuantityDiscountTable =
    (hasSalePriceButNoDivideByQuantity ||
      hasNoSalePriceButHasDivideByQuantity ||
      !divideQuantity) &&
    (!hasMAPStyle || isUserQualifiedForMapPricing) &&
    discountsWithLowerPriceAndHigherStartBuy.length > 0;

  const isWishlistPage =
    window?.location?.pathname.includes("wishlist") ?? false;

  function renderPlusPricing() {
    return (
      <div>
        <PlusMemberDiscount
          productListingView={productListingView}
          selectedProduct={selectedProduct}
          user={user}
        />
        {displaySalePrice && (
          <SaleBadgeAndPricing
            dataTestId="itemSale"
            price={price.salePrice}
            salePricingClassNames={cls(
              "p-0",
              "my-1",
              productListingView === "grid" ? "pt-1" : "pt-2 xs:py-1",
              "text-xs",
              "text-gray-800",
              "font-normal",
            )}
            saleBadgeExtraClassNames={cls("px-1", "mr-1", "py-0.5")}
            labelClassnames="text-red-500 font-bold"
            hideRegularLabel
          />
        )}
      </div>
    );
  }

  function renderExclusivePlusPricing() {
    return (
      <ExclusivePlusPricing
        mapPrice={price.minimumAdvertisedPriceProperties.price}
        p1Price={price.price}
        unitOfMeasure={unitOfMeasure}
        hasQuantityDiscounts={
          price.hasQuantityDiscounts && shouldDisplayQuantityDiscountTable
        }
        divideByUnit={divideByUnit}
        divideQuantity={divideQuantity}
      />
    );
  }

  function renderDefaultPricing() {
    return (
      <div>
        <Price
          price={isUserQualifiedForMapPricing ? lowestPrice : price.price}
          productListingView={productListingView}
          unitOfMeasure={unitOfMeasure}
          displaySalePrice={displaySalePrice}
          salePrice={price.salePrice}
          quantityDiscountPrice={
            hasApplicableQuantityDiscountPrice
              ? applicableQuantityDiscountPrice
              : undefined
          }
        />
        <DivideByPricing
          dataTestId="divideByPricingQuantityDiscount"
          price={
            isUserQualifiedForMapPricing || activeSale
              ? lowestPrice
              : price.price
          }
          divideByUnit={divideByUnit}
          divideQuantity={divideQuantity}
        />
      </div>
    );
  }

  const plusPrice =
    selectedProduct.price.minimumAdvertisedPriceOverride?.priceLevel5;

  let mainPricingContent: React.ReactNode;

  if (
    showPlusPricing &&
    (!hasApplicableQuantityDiscountPrice ||
      plusPrice < applicableQuantityDiscountPrice)
  ) {
    mainPricingContent = renderPlusPricing();
  } else if (showExclusivePlusPricing) {
    mainPricingContent = renderExclusivePlusPricing();
  } else {
    mainPricingContent = renderDefaultPricing();
  }

  return (
    <div className={isWishlistPage ? "flex flex-col justify-center" : ""}>
      {mainPricingContent}
      {shouldDisplayQuantityDiscountTable && !hideQuantityDiscountTable && (
        <QuantityDiscountsTable
          hasLotDiscounts={
            price.hasLotQuantityDiscounts || price.hasSingleLotQuantityDiscount
          }
          quantityDiscounts={discountsWithLowerPriceAndHigherStartBuy}
          startBuy={startBuy}
          productPrice={
            isUserQualifiedForMapPricing || activeSale
              ? lowestPrice
              : price.price
          }
          mustBuy={mustBuy}
          productListingView={productListingView}
          selectedProductItemNumber={itemNumber}
          unitOfMeasure={unitOfMeasure}
          divideByUnit={divideByUnit}
          divideQuantity={divideQuantity}
          setIsQuantityDiscountPopoverOpen={setIsQuantityDiscountPopoverOpen}
          productItemLink={link}
        />
      )}
      <meta content="USD" />
    </div>
  );
};

export default QuantityDiscounts;
